
import { createNamespacedHelpers } from '@/common/vuex-composition-hooks';
import {
  computed,
  defineComponent,
  onBeforeMount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { IAuthActions, IAuthGetters, IAuthState } from '../login/types';
import ListFilter from '@/components/ListFilter.vue';
import { useRouter } from 'vue-router';
import ListFilterUnits from '@/components/ListFilterUnits.vue';
import ListFilterBrands from '@/components/ListFilterBrand.vue';
import ListFilterSegments from '@/components/ListFilterSegments.vue';
import { IDasktopActions, IDasktopState } from './types';
import { IDashboardActions, IDashboardState } from '../dashboard/types';
import FilterModal from '@/components/FilterModal.vue';
import { DassIcon } from 'ds-ui';
import Tabs from '@/modules/desktop/components/DesktopTabs.vue';

interface IList {
  text: string;
}
const { useActions, useGetters, useState, useMutations } =
  createNamespacedHelpers<IAuthState, IAuthGetters, IAuthActions>('auth');

const { useActions: useActionDashboard } = createNamespacedHelpers<
  IDashboardState,
  null,
  IDashboardActions
>('dashboard');

const { useState: useStateFilter, useActions: useActionsFilter } =
  createNamespacedHelpers<IDasktopState, null, IDasktopActions>('desktop');

export default defineComponent({
  components: {
    Tabs,
    ListFilter,
    ListFilterUnits,
    ListFilterBrands,
    ListFilterSegments,
    FilterModal,
    DassIcon,
  },

  setup() {
    const router = useRouter();

    const { setBillingUnits } = useActions(['setBillingUnits']);

    const { SET_ACCESS_TYPE } = useMutations(['SET_ACCESS_TYPE']);
    const { alterFilters, saveFilter, allFilter } = useActionsFilter([
      'alterFilters',
      'saveFilter',
      'allFilter',
    ]);
    const { filtersEcomm } = useStateFilter(['filtersEcomm']);
    const { loadingBillingUnits } = useState(['loadingBillingUnits']);
    const { sessionUser } = useGetters(['sessionUser']);
    const { billingUnitAccess } = useState(['billingUnitAccess']);

    onBeforeMount(async () => {
      SET_ACCESS_TYPE('desktop');
    });

    const checkedAll = ref(false);

    const units = computed((): IList[] =>
      sessionUser.value?.billingUnits.map((unit) => {
        return { text: unit.fullName };
      })
    );

    const unitsFilter = ref([]);

    unitsFilter.value = sessionUser.value?.billingUnits
      .filter((el) =>
        sessionUser.value?.billingUnitAccess?.includes(el.billingUnitCode)
      )
      .map((el) => el.fullName) as any;

    const unitsSelected = computed({
      get: () => unitsFilter.value,

      set: (value) => {
        unitsFilter.value = value;
      },
    });

    const refTest = ref(unitsSelected.value);

    const filterUnits: any = computed(() => filtersEcomm.value.billingUnits);
    const filterBrands: any = computed(() => filtersEcomm.value.brands);
    const filterSegments: any = computed(() => filtersEcomm.value.segments);
    const stbillingUnitAccess: any = computed(() => billingUnitAccess?.value);

    const currentFormattedDate = () => {
      const date = new Date().toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });

      return date;
    };

    onMounted(() => (checkedAll.value = true));

    const isEcomm = computed(
      () => router?.currentRoute?.value.name !== 'Ecommerce-Desktop'
    );

    const tagsUnits = (event) => {
      if (event.some((el) => el.checked === false)) {
        filterTagEcommUnit.value = [
          ...event.filter((el) => el.checked === true).map((el) => el.fullName),
        ] as any;
        return;
      }
      filterTagEcommUnit.value = ['Todas as Unidades'];
    };

    const tagsBrands = (event) => {
      if (event.some((el) => el.checked === false)) {
        filterTagEcommBrand.value = [
          ...event.filter((el) => el.checked === true).map((el) => el.name),
        ] as any;
        return;
      }
      filterTagEcommBrand.value = ['Todas as Marcas'];
    };

    const tagsSegments = (event) => {
      if (event.some((el) => el.checked === false)) {
        filterTagEcommSegments.value = [
          ...event.filter((el) => el.checked === true).map((el) => el.name),
        ] as any;
        return;
      }
      filterTagEcommSegments.value = ['Todos os Segmentos'];
    };

    const filterTag = ref([]);
    const filterTagEcommUnit = ref<string[]>(['Todas as Unidades']);
    const filterTagEcommBrand = ref<string[]>(['Todas as Marcas']);
    const filterTagEcommSegments = ref<string[]>(['Todos os Segmentos']);

    watch(
      [filterTagEcommUnit, filterTagEcommBrand, filterTagEcommSegments],
      () =>
        (filterTagEcomm.value = [
          ...filterTagEcommUnit.value,
          ...filterTagEcommBrand.value,
          ...filterTagEcommSegments.value,
        ])
    );

    const filterTagEcomm = ref<string[]>([
      'Todas as Unidades',
      'Todas as Marcas',
      'Todos os Segmentos',
    ]);

    watch(isEcomm, () => {
      if (isEcomm.value)
        filterTagEcomm.value = [
          'Todas as Unidades',
          'Todas as Marcas',
          'Todos os Segmentos',
        ];
    });

    const cancelEcomm = async () => {
      await saveFilter(false);

      filterTagEcomm.value = [
        'Todas as Unidades',
        'Todas as Marcas',
        'Todos os Segmentos',
      ];
    };

    const alterationCheckAll = ref(true);

    const allfilters = async (typeFilter) => {
      if (!typeFilter.checked) {
        alterationCheckAll.value = false;
      } else {
        alterationCheckAll.value = true;
        alterationFilter.value = true;
      }
      if (typeFilter.filterName === 'billingUnit')
        await allFilter({
          filter: 'all',
          code: 'billingUnits',
          checked: typeFilter.checked,
        });

      if (typeFilter.filterName === 'brand')
        await allFilter({
          filter: 'all',
          code: 'brands',
          checked: typeFilter.checked,
        });

      if (typeFilter.filterName === 'segment')
        await allFilter({
          filter: 'all',
          code: 'segments',
          checked: typeFilter.checked,
        });
    };

    const checkAllFilter = (isAllUnits) => {
      if (isAllUnits) {
        filterTag.value = ['Todas as unidades'] as any;
        alterationFilterUnits.value = true;
        unitCodes.value = sessionUser.value?.billingUnits.map(
          (unit) => unit.billingUnitCode
        ) as any;

        unitsFilter.value = sessionUser.value?.billingUnits.map(
          (unit) => unit.fullName
        ) as any;
      }
    };

    onMounted(() => {
      filterTag.value =
        sessionUser.value?.billingUnitAccess?.length ===
        sessionUser.value?.allBillingUnitAccess?.length
          ? ['Todas as Unidades']
          : (sessionUser.value?.billingUnits
              .filter((el) =>
                sessionUser.value?.billingUnitAccess?.includes(
                  el.billingUnitCode
                )
              )
              .map((el) => el.fullName) as any);
    });

    const unitCodes = ref([]);

    const alterationFilterUnits = ref(false);

    const changeFilter = async (event) => {
      alterationFilterUnits.value = true;
      filterTag.value =
        units.value.length === event.length ? ['Todas as unidades'] : event;
      unitCodes.value = event.map(
        (el) =>
          sessionUser.value.billingUnits.filter(
            (unit) => unit.fullName === el
          )[0].billingUnitCode
      );
    };

    const saveChanges = async () => {
      if (!unitCodes.value.length) return;
      modalFilter.value = false;
      alterationFilterUnits.value = false;
      await setBillingUnits(unitCodes.value, true);
      unitCodes.value = [];
      unitsFilter.value = sessionUser.value?.billingUnits
        .filter((el) => stbillingUnitAccess?.value.includes(el.billingUnitCode))
        .map((el) => el.fullName) as any;
    };

    const saveChangesEcomm = async () => {
      modalFilter.value = false;
      await saveFilter(true);
      await reloadEcomm();
    };

    const cancel = ref(false);

    const alterationFilter = ref(false);

    const { fetchEcommerceData } = useActionDashboard(['fetchEcommerceData']);

    const reloadEcomm = async () => {
      if (alterationFilter.value) {
        await fetchEcommerceData();
        alterationFilter.value = false;
      }
    };

    const changedFilter = async (filter, type) => {
      alterationFilter.value = true;
      alterationCheckAll.value = true;

      if (type === 'units')
        await alterFilters({
          filter: 'billingUnit',
          code: filter.code.toString(),
          checked: filter.checked,
        });

      if (type === 'brand')
        await alterFilters({
          filter: 'brand',
          code: filter.code.toString(),
          checked: filter.checked,
        });

      if (type === 'segment')
        await alterFilters({
          filter: 'segment',
          code: filter.code.toString(),
          checked: filter.checked,
        });
    };

    const eventStop = (e) => {
      e.stopPropagation();
    };

    const modalFilter = ref(false);

    const openModal = () => {
      modalFilter.value = true;
    };

    const closeModal = async () => {
      await saveFilter(false);
    };

    return {
      currentFormattedDate,
      units,
      checkedAll,
      unitsSelected,
      changeFilter,
      isEcomm,
      loadingBillingUnits,
      filterUnits,
      filterBrands,
      filterSegments,
      changedFilter,
      reloadEcomm,
      eventStop,
      alterationFilter,
      modalFilter,
      openModal,
      filterTag,
      checkAllFilter,
      saveChanges,
      cancel,
      filterTagEcomm,
      tagsUnits,
      tagsBrands,
      tagsSegments,
      saveChangesEcomm,
      cancelEcomm,
      alterationFilterUnits,
      closeModal,
      refTest,
      unitsFilter,
      allfilters,
      alterationCheckAll,
    };
  },
});
